import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FaArrowRight,
  FaCheck,
  FaClipboardList,
  FaCopy,
  FaFingerprint,
  FaIdCard,
  FaMapMarkerAlt,
  FaQrcode,
  FaRegCheckCircle,
  FaShieldAlt,
  FaUserEdit,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { userSelector } from "./UserSlice";
import AuthNavBar from "../components/nav/AuthNav";
import "./Dashboard.css";

const Dashboard = () => {
  const { t } = useTranslation();
  const { isFetching } = useSelector(userSelector);
  const [copied, setCopied] = useState(false);
  const firstName = localStorage.getItem("firstNameFromServer") || "Citizen";
  const lastName = localStorage.getItem("lastNameFromServer") || "";
  const trackingId = localStorage.getItem("trackingIDFromServer");
  const hasTrackingId = trackingId && trackingId !== "null";
  const primaryAction = hasTrackingId
    ? { label: t("review_your_details"), to: "/view-details" }
    : { label: t("start_preenrollment"), to: "/biodata" };

  const copyTrackingId = async () => {
    if (!hasTrackingId) {
      return;
    }

    try {
      await navigator.clipboard.writeText(trackingId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      setCopied(false);
    }
  };

  const stages = [
    {
      label: t("stage_account_verified"),
      detail: t("stage_otp_confirmed"),
      state: "complete",
    },
    {
      label: t("stage_demographic_details"),
      detail: hasTrackingId ? t("stage_submitted") : t("stage_next_step"),
      state: hasTrackingId ? "complete" : "active",
    },
    {
      label: t("stage_qr_code"),
      detail: t("stage_qr_generated"),
      state: hasTrackingId ? "active" : "pending",
    },
    {
      label: t("stage_biometric_visit"),
      detail: t("stage_enrollment_centre"),
      state: "pending",
    },
  ];

  if (isFetching) {
    return (
      <div className="dashboard-page dashboard-loading">
        <div
          className="dashboard-loader"
          aria-label={t("loading")}
        />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <AuthNavBar />
      <main className="dashboard-main">
        <section
          className="dashboard-welcome"
          aria-labelledby="dashboard-heading"
        >
          <div className="dashboard-welcome-copy">
            <div className="dashboard-eyebrow">
              <span /> {t("bioenroll_workspace")}
            </div>
            <h1 id="dashboard-heading">
              {t("welcome_back_name", { name: firstName })}
            </h1>
            <p>
              {hasTrackingId
                ? t("dashboard_progress_text")
                : t("dashboard_ready_text")}
            </p>
            <Link className="dashboard-primary-action" to={primaryAction.to}>
              {primaryAction.label} <FaArrowRight aria-hidden="true" />
            </Link>
          </div>
          <div className="dashboard-status-card">
            <div className="status-icon">
              <FaRegCheckCircle />
            </div>
            <div>
              <span className="status-label">{t("enrollment_status")}</span>
              <strong>
                {hasTrackingId
                  ? t("preenrollment_in_progress")
                  : t("ready_to_begin")}
              </strong>
              <small>
                {hasTrackingId
                  ? t("account_details_on_record")
                  : t("complete_form_qr")}
              </small>
            </div>
          </div>
          <FaFingerprint className="dashboard-fingerprint" aria-hidden="true" />
        </section>

        <section className="dashboard-grid">
          <article className="journey-panel" aria-labelledby="journey-heading">
            <div className="panel-heading">
              <div>
                <div className="dashboard-eyebrow">
                  <span /> {t("your_journey")}
                </div>
                <h2 id="journey-heading">{t("preenrollment_progress")}</h2>
              </div>
              <span className="journey-state">
                {hasTrackingId ? t("in_progress") : t("step_1_of_4")}
              </span>
            </div>
            <ol className="journey-steps">
              {stages.map((stage, index) => (
                <li className={`journey-step ${stage.state}`} key={stage.label}>
                  <span className="journey-marker">
                    {stage.state === "complete" ? <FaCheck /> : index + 1}
                  </span>
                  <span>
                    <strong>{stage.label}</strong>
                    <small>{stage.detail}</small>
                  </span>
                </li>
              ))}
            </ol>
          </article>

          <aside className="tracking-panel" aria-labelledby="tracking-heading">
            <div className="dashboard-eyebrow">
              <span /> {t("application_tracking")}
            </div>
            <h2 id="tracking-heading">
              {hasTrackingId
                ? t("your_tracking_id")
                : t("track_your_application")}
            </h2>
            {hasTrackingId ? (
              <>
                <div className="tracking-id-value">
                  <span>{trackingId}</span>
                  <button
                    type="button"
                    onClick={copyTrackingId}
                    aria-label={t("your_tracking_id")}
                  >
                    <FaCopy />
                  </button>
                </div>
                <p>
                  {copied
                    ? t("tracking_id_copied")
                    : t("tracking_id_usage")}
                </p>
              </>
            ) : (
              <p>{t("tracking_id_after_submission")}</p>
            )}
            <Link to="/track-enrolment" className="tracking-link">
              {t("track_enrollment_btn")} <FaArrowRight />
            </Link>
          </aside>
        </section>

        <section
          className="dashboard-actions"
          aria-labelledby="actions-heading"
        >
          <div className="panel-heading">
            <div>
              <div className="dashboard-eyebrow">
                <span /> {t("quick_actions")}
              </div>
              <h2 id="actions-heading">{t("continue_enrollment")}</h2>
            </div>
          </div>
          <div className="action-grid">
            <Link
              className="action-card"
              to={hasTrackingId ? "/view-details" : "/biodata"}
            >
              <span className="action-icon green">
                <FaClipboardList />
              </span>
              <span>
                <strong>
                  {hasTrackingId
                    ? t("review_your_details")
                    : t("complete_demographic_data")}
                </strong>
                <small>
                  {hasTrackingId
                    ? t("review_application_info")
                    : t("add_preenrollment_info")}
                </small>
              </span>
              <FaArrowRight className="action-arrow" />
            </Link>
            <Link
              className="action-card"
              to={hasTrackingId ? "/qrgenerator" : "/biodata"}
            >
              <span className="action-icon blue">
                <FaQrcode />
              </span>
              <span>
                <strong>{t("access_qr_code")}</strong>
                <small>
                  {hasTrackingId
                    ? t("prepare_biometric_visit")
                    : t("available_after_submission")}
                </small>
              </span>
              <FaArrowRight className="action-arrow" />
            </Link>
            <Link className="action-card" to="/track-enrolment">
              <span className="action-icon amber">
                <FaMapMarkerAlt />
              </span>
              <span>
                <strong>{t("find_your_status")}</strong>
                <small>{t("follow_application")}</small>
              </span>
              <FaArrowRight className="action-arrow" />
            </Link>
            <Link
              className="action-card"
              to={hasTrackingId ? "/modification" : "/help2"}
            >
              <span className="action-icon rose">
                {hasTrackingId ? <FaUserEdit /> : <FaShieldAlt />}
              </span>
              <span>
                <strong>
                  {hasTrackingId ? t("request_update") : t("need_help")}
                </strong>
                <small>
                  {hasTrackingId
                    ? t("request_update_info")
                    : t("get_support")}
                </small>
              </span>
              <FaArrowRight className="action-arrow" />
            </Link>
          </div>
        </section>

        <section
          className="center-notice"
          aria-label={t("bring_qr_notice")}
        >
          <FaIdCard aria-hidden="true" />
          <span>
            <strong>{t("bring_qr_notice")}</strong>
            <small>{t("bring_qr_notice_detail")}</small>
          </span>
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
